import { Component, Input, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NewTripService } from "@app/trip-new/shared/trip.service";
import { ToastrService } from "ngx-toastr";
import { LazyLoadEvent } from "primeng/api";
import { Table } from "primeng/table";
import { debounceTime, Observable, Subject, Subscription } from "rxjs";
import { TripSignalrService } from "@app/trip-new/shared/trip-signalr.service";
import { DriverService } from "@app/driver/shared/driver.service";

@Component({
  selector: "lgt-grid",
  templateUrl: "./lgt-grid.component.html",
  styleUrls: ["./lgt-grid.component.css"],
})
export class LgtGridComponent {
  @ViewChild("dt", { static: true }) dataTable!: Table;

  @Input() cols!: any[];
  @Input() selectedColumns: any[] = [];
  @Input() selectCol: boolean = false;
  @Input() data!: any[];
  @Input() onRowClick?: (value: any) => any;
  @Input() onDblClick!: (value: any) => any;
  @Input() showPrintInvoice: boolean = false;
  @Input() onPrintInvoice!: (selectedTrips: any[], filters: any, selectAll: boolean) => any;
  @Input() onImport!: (value: any) => any;
  @Input() onUploadFilesForNewTrip!: (value: any) => any;
  @Input() showNewButton: boolean = false;
  @Input() newButtonOptions!: any[];
  @Input() newButtonAction!: () => any;
  @Input() excelFileName: string = "";
  @Input() filters!: any;
  @Input() loadData!: (filters: any, rows: number, first: number, sortField: string, sortOrder: number) => any;
  @Input() showRightButton: boolean = false;
  @Input() onMoveRight!: (selectedTripsIds: any[]) => Observable<any>;
  @Input() rightArrowText: string = "";
  @Input() showLeftButton: boolean = false;
  @Input() onMoveLeft!: (selectedTripsIds: any[]) => Observable<any>;
  @Input() leftArrowText: string = "";
  @Input() useSignalR: boolean = false;
  @Input() showExcelButton: boolean = false;
  @Input() onExcelButton!: (selectedColumns: any[], selectedTrips: any[], filters: any, selectAll: boolean) => any;
  @Input() pageName: string = "";
  @Input() summaryLabel: string = "Selected";
  @Input() summaryValue: number | null = null;

  selectAll: any = false;
  isSelectAllClicked: any = false;
  selectfilterQuantity: number = 0;
  rowsfilterQuantity: number = 0;
  sortField: string = "";
  sortOrder: number = 1;
  totalRecords: number = 0;

  // Pagination properties
  first: number = 0;
  rows: number = 25;
  rowsOptions: number[] = [25, 50, 100];

  filtersSubject = new Subject();
  filtersSubscription!: Subscription;

  items: any[] = [];

  isLoading = true;

  selectedTrips: any[] = [];
  allTripIds: any[] = [];

  activeTripList: any = {};

  constructor(
    private router: Router,
    private tripService: NewTripService,
    private _driverList: DriverService,
    private toastr: ToastrService,
    private signalRService: TripSignalrService,
  ) {}

  ngOnInit() {
    this.rowsfilterQuantity = this.data.length;
    if(this.selectedColumns.length === 0){
      this.selectedColumns = this.cols;
    }
    this.filtersSubscription = this.filtersSubject.pipe(debounceTime(500)).subscribe(() => {
      this.dataTable.first = 0;
      this.dataTable.firstChange.emit(this.dataTable.first);
      this.dataTable.onLazyLoad.emit(this.dataTable.createLazyLoadMetadata());
      this.clearAllSelected();
    });
    if (this.useSignalR) {
      this.setupSignalRListeners();
    }
    const savedOrder = localStorage.getItem(this.pageName + 'pTableColumnOrder');
    if (savedOrder) {
        this.selectedColumns = JSON.parse(savedOrder);
    } else {
        this.selectedColumns = this.cols; // Default order
    }
  }

  saveColumnOrdering(event: any) {
    const reorderedColumns = event.columns;
    try {
      localStorage.setItem(this.pageName + 'pTableColumnOrder', JSON.stringify(reorderedColumns));
    } catch (e) {
      // Ignore QuotaExceededError - column order won't persist but app continues
    }
  }

  setupSignalRListeners() {
    this.signalRService.subscribeToAllTrips().then(() => {
      this.signalRService.allActiveTrips.subscribe((allTrips) => {
        this.activeTripList = allTrips;
        this.updateActiveTrips();
      });
    });
  }
  updateActiveTrips() {
    if(this.useSignalR) {
      this.data.forEach((trip) => {
        if (this.activeTripList[trip.id]) {
          trip.activeUsers = this.activeTripList[trip.id];
          trip.activeUsersTooltip = `Active users: ${this.activeTripList[trip.id].join(", ")}`;
        } else {
          trip.activeUsers = [];
          trip.activeUsersTooltip = "No active users";
        }
      });
    }
  }

  ngOnDestroy() {
    this.filtersSubscription.unsubscribe();
  }

  createTripChargeColumns(trips: any) {
    const tripSet = {};
    for (const trip of trips) {
      for (const charge of trip.tripCharges) {
        trip[`${charge.chargeTypeName}-MILES`] = charge.miles;
        trip[`${charge.chargeTypeName}-UNITS`] = charge.units;
        trip[`${charge.chargeTypeName}-RATE`] = charge.rate;
        trip[`${charge.chargeTypeName}-PRICE`] = charge.price;
      }

      (tripSet as any)[trip.id] = trip;
    }
    return Object.values(tripSet);
  }

  convertToServerSideFilters(data: any){
    for(const item of Object.keys(data)){
      this.filters[item] = data[item].value;
    }
  }

  onLazyLoad(event: LazyLoadEvent) {
    this.convertToServerSideFilters(event.filters);
    const observable = this.loadData(
      this.filters,
      event.rows ?? 0,
      event.first ?? 0,
      event.sortField ?? '',
      event.sortOrder ?? 0
    );

    this.isLoading = true;
    observable.subscribe((result: any) => {
      result.records = result.records.map((trip: any) => ({
        ...trip,
        actualDeliveryDate: trip.actualDeliveryDate ? new Date(trip.actualDeliveryDate) : null,
        invoiceDate: trip.invoiceDate ? new Date(trip.invoiceDate) : null,
        efsEffectiveDate: trip.efsEffectiveDate ? new Date(trip.efsEffectiveDate) : null,
        hiredDate: trip.hiredDate ? new Date(trip.hiredDate) : null,
        margin: trip.netCharges - trip.netPayment,
        marginPercent: trip.netCharges
          ? ((trip.netCharges - trip.netPayment) / trip.netCharges) * 100
          : 0,
        selected: this.selectAll || this.selectedTrips.includes(trip.id),
      }));
      if(result.records[0]?.tripCharges){
        this.data = this.createTripChargeColumns(result.records);
      } else {
        this.data = result.records;
      }
      this.rowsfilterQuantity = result.totalRecords;
      this.totalRecords = result.totalRecords;
      this.allTripIds = result.ids;
      this.isLoading = false;
    });
  }

  onClickUpload() {}

  filterBySearchTerm(searchTerm: string, field: string) {
    if (!searchTerm) return;

    if (this.dataTable) {
      this.dataTable.filter(searchTerm, field, "contains");
      this.filters[field] = searchTerm;
    }
  }

  clearFilters() {
    for (let col of this.cols) {
      this.clearFilter(col.field);
      if (col.type === 'date') {
        this.clearFilter(col.field + '_from');
        this.clearFilter(col.field + '_to');
      }
    }
  }

  clearFilter(field: any) {
    this.dataTable.filter("", field, "contains");
    this.filters[field] = "";
    // For date range, also clear the filter for that field if needed
    if (field.endsWith('_from') || field.endsWith('_to')) {
      this.filters[field] = "";
    }
  }

  dictToArr(dict: any) {

    //return list of keys in dict where dict[key] is not empty
    return Object.keys(dict).filter((key) => dict[key] !== undefined && dict[key] !== null && dict[key] !== ""); 
  }

  isFilterable(field: string) {
    return Object.keys(this.filters).includes(field);
  }

  fieldToLabel(field: any) {
    //if field ends with _from return header for col with _from stripped
    if (field.endsWith('_from')) {
      return this.cols.find((col) => col.field === field.substring(0, field.length - 5)).header + " (From)";
    } else if (field.endsWith('_to')) {
      return this.cols.find((col) => col.field === field.substring(0, field.length - 3)).header + " (To)";
    }
    return this.cols.find((col) => col.field === field).header;
  }

  onSort(event: any) {
    this.sortField = event.field;
    this.sortOrder = event.order;
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  onRowsChange(event: any) {
    this.rows = event.target.value;
    this.first = 0; // Reset to the first page
  }

  isSelected(rowData: any) {
    return this.selectedTrips.includes(rowData.id);
  }

  selectionChanged(isTripSelected: any, rowData: any) {
    let visibleRows = this.dataTable.filteredValue ? this.dataTable.filteredValue : this.data;
    this.rowsfilterQuantity = visibleRows.length;
    this.selectfilterQuantity += isTripSelected.checked == true ? 1 : -1;

    if (isTripSelected.checked) {
        this.selectedTrips.push(rowData.id); // Save the ID of the selected row
    } else {
        this.selectedTrips = this.selectedTrips.filter(id => id !== rowData.id); // Remove the ID if unselected
    }

    if (this.totalRecords <= this.selectfilterQuantity) {
        this.selectAll = true;
    } else {
        this.selectAll = false;
        this.isSelectAllClicked = false;
    }
  }

  selectAllTripsChanged(isSelectAllClicked: any) {
    this.selectAll = isSelectAllClicked;
    this.isSelectAllClicked = isSelectAllClicked;
    this.select("click");
    if(isSelectAllClicked) {
      this.selectfilterQuantity = +this.totalRecords;
      this.selectedTrips = this.allTripIds;
    } else {
      this.selectfilterQuantity = 0;
      this.selectedTrips = [];
    }
  }

  select(type: any) {
    let visibleRows = this.dataTable.filteredValue ? this.dataTable.filteredValue : this.data;
    this.rowsfilterQuantity = visibleRows.length;
    let found = false;
    if (this.isEmpty(this.dataTable.filters)) {
      if (this.isSelectAllClicked == true) {
        this.data.map((trip) => (trip.selected = true));
        this.selectfilterQuantity = this.data.length;
      } else {
        if (type == "click") {
          this.data.map((trip) => (trip.selected = false));
          this.selectfilterQuantity = 0;
        }
      }
    } else {
      if (this.selectAll == true) {
        for (let i = 0; i < this.data.length; i++) {
          found = false;
          for (let j = 0; j < visibleRows.length; j++) {
            if (visibleRows[j].tripID == this.data[i].tripID) {
              this.data[i].selected = true;
              found = true;
              break;
            }
          }
          if (found == false) {
            this.data[i].selected = false;
          }
        }
        this.selectfilterQuantity = visibleRows.length;
      } else {
        if (type == "click") {
          this.data.map((trip) => (trip.selected = false));
          this.selectfilterQuantity = 0;
        }
      }
    }
  }

  onClickLeftArrow() {
    this.onMoveLeft(this.data).subscribe(
      (result) => {
        this.toastr.success("Trips updated");
        this.clearAllSelected();
        this.dataTable.first = 0;
        this.dataTable.firstChange.emit(this.dataTable.first);
        this.dataTable.onLazyLoad.emit(this.dataTable.createLazyLoadMetadata())
      },
      (error) => {
        this.toastr.error(error, "Error");
      }
    );
  }

  onClickRightArrow() {
    this.onMoveRight(this.data).subscribe(
      (result) => {
        this.toastr.success("Trips updated");
        this.clearAllSelected();
        this.dataTable.first = 0;
        this.dataTable.firstChange.emit(this.dataTable.first);
        this.dataTable.onLazyLoad.emit(this.dataTable.createLazyLoadMetadata())
      },
      (error) => {
        this.toastr.error(error, "Error");
      }
    );
  }

  clearAllSelected() {
    this.selectAll = false;
    this.isSelectAllClicked = false;
    this.select("click");
    this.selectfilterQuantity = 0;
    this.selectedTrips = [];
  }

  excelGeneration() {
    this.onExcelButton(this.selectedColumns, this.selectedTrips, this.filters, this.selectAll);
  }

  printInvoices(){
    this.onPrintInvoice(this.selectedTrips, this.filters, this.selectAll);
  }

  isEmpty(obj: any) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  onColumnChange(event: any) {
    if (event.originalEvent.selected) {
      this.selectedColumns.unshift(event.itemValue);
      this.selectedColumns.pop();
    }
    try {
      localStorage.setItem(this.pageName + 'pTableColumnOrder', JSON.stringify(this.selectedColumns));
    } catch (e) {
      // Ignore QuotaExceededError
    }
  }

  onDateRangeChange(col: any, event: any, before: boolean) {
    this.dataTable.filter(event.target.value, col, before ? "greaterThan" : "lessThan");
    this.filtersSubject.next(event);
  }

  onFilterInput(col: any, event: any) {
    this.dataTable.filter(event.target.value, col, "contains");
    this.filtersSubject.next(event);
  }

  handleRowClick(event: MouseEvent, rowData: any) {
    if (!this.onRowClick) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest("p-checkbox, .p-checkbox, input, button, a")) {
      return;
    }

    this.onRowClick(rowData);
  }

  formatDateValue(value: any): string {
    if (!value) {
      return "";
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleDateString();
  }
}
